import { Box, Button, Chip, Link, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { theme } from '@store';
import { useReport } from './hooks';
import { Filters, Fields } from './components';
import { isNil } from 'lodash';
import { reportTypeOptions } from '@views/pages/Reporting/Reports/types.ts';
import { TruncatedTextWithConditionalTooltip } from '@components';

export const Report = () => {
  const {
    report,
    organization,
    setIsModalOpen,
    handleNavigateToPreview,
    handleSaveReport,
    handleDiscard,
    hideSaveOptions,
    isPending,
    isOwner,
  } = useReport();

  const reportType = reportTypeOptions.find(
    o => o.value === report?.report_type,
  );

  const isPreviewDisabled =
    report?.options?.select.length === 0 ||
    isPending ||
    (report?.interval === 'custom' &&
      isNil(report.custom_timeframe?.from_date) &&
      isNil(report.custom_timeframe?.to_date));

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            width: 'calc(100% - 285px)',
          }}
        >
          <TruncatedTextWithConditionalTooltip
            text={report?.name}
            variant='h4'
            width='80%'
          />
          <Link
            sx={{
              cursor: 'pointer',
              pointerEvents: !isOwner ? 'none' : 'auto',
              color: !isOwner ? 'gray' : 'inherit',
            }}
            onClick={() => setIsModalOpen(true)}
          >
            Edit
          </Link>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '0.5rem',
            width: '275px',
          }}
        >
          <Button variant='text' onClick={handleDiscard}>
            DISCARD
          </Button>
          <Button
            variant='outlined'
            onClick={handleNavigateToPreview}
            disabled={isPreviewDisabled}
          >
            RUN
          </Button>
          {!hideSaveOptions && (
            <Button
              variant='contained'
              color='secondary'
              sx={{
                color: theme.palette.common.white,
              }}
              onClick={handleSaveReport}
              disabled={isPreviewDisabled}
            >
              RUN & SAVE
            </Button>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          mt: 1,
        }}
      >
        {report?.report_folder?.organization?.name && (
          <Typography variant='body1' sx={{ color: 'text.secondary' }}>
            {organization?.name ?? '-'}
          </Typography>
        )}
        <Typography sx={{ color: 'text.secondary' }}>•</Typography>
        {report?.report_folder?.name && (
          <Typography variant='body1' sx={{ color: 'text.secondary' }}>
            {report?.report_folder?.name ?? '-'}
          </Typography>
        )}
        <Typography sx={{ color: 'text.secondary' }}>•</Typography>
        {report?.report_type && (
          <Typography variant='body1' sx={{ color: 'text.secondary' }}>
            {reportType?.label ?? report?.report_type}
          </Typography>
        )}
        <Chip
          icon={report?.private ? <LockIcon /> : <LockOpenIcon />}
          label={report?.private ? 'Private' : 'Public'}
          sx={{
            height: 24,
            ml: 1,
            '& .MuiChip-icon': {
              width: 16,
              height: 16,
            },
          }}
        />
      </Box>
      <Typography variant='body2' sx={{ mt: 1 }}>
        {report?.description}
      </Typography>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          gap: '2rem',
          mt: '1rem',
          borderTop: `1px solid ${theme.palette.grey[300]}`,
        }}
      >
        <Filters />
        <Fields />
      </Box>
    </>
  );
};
